import React from 'react';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {isOk} from '../../../utils/apiResponse';
import {ScreenTracker} from '../../../utils/ScreenTracker';
import {formatShortDate} from '../../agency/connections/reconnectAccountText';

/**
 * «Vincular cuenta» reconoce la cuenta que no aparece porque YA está vinculada al cliente y perdió el
 * acceso (M6 de la tanda «Reconexión de pauta», UD-21). `get-accounts/<canal>` no la ofrece (deja
 * afuera las cuentas con error confirmado) y la búsqueda decía «No hay resultados»: el usuario la daba
 * por perdida y escribía a soporte.
 *
 * La fuente es `client/<id>/ad_accounts/<canal>` (contrato §5), que ya filtra por agencia y por los
 * clientes que el usuario ve, y trae `has_access`. Sin cambios en el backend.
 */

const RAW = {escapeValue: false};

/** Las cuentas sin acceso de ese cliente y canal. Nunca rechaza: si el pedido falla, ninguna. */
export function fetchUnreachableLinked(clientId, channel) {
    if (!clientId || !channel) return Promise.resolve([]);
    const url = `${API_URL}client/${encodeURIComponent(clientId)}/ad_accounts/${encodeURIComponent(channel)}`;
    // Adentro de la cadena: ni un error al armar el pedido puede romper el modal que lo acompaña.
    return Promise.resolve()
        .then(() => new APIClient(url).call('GET'))
        .then((response) => (isOk(response) && Array.isArray(response.data)
            ? response.data.filter((row) => row && row.has_access === false && row.channel === channel)
            : []))
        .catch(() => []);
}

function normalized(value) {
    return String(value === null || value === undefined ? '' : value)
        .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** La primera cuenta cuyo nombre o id contiene lo buscado, sin distinguir mayúsculas ni tildes. */
export function findUnreachable(rows, query) {
    const needle = normalized(query);
    if (!needle) return null;
    return (rows || []).find((row) => normalized(row.name).includes(needle)
        || normalized(row.account_id).includes(needle)) || null;
}

/** «"black optica" ya está vinculada a Óptica Black, pero perdimos el acceso el 23/10, 16:04.» */
export function unreachableLinkedText(t, row, language) {
    const date = formatShortDate(row.no_access_since_at, language);
    const opts = {account: row.name || row.account_id, client: row.agency_client_name, date, interpolation: RAW};
    return t(date ? 'control_central.link_account_unreachable' : 'control_central.link_account_unreachable_undated', opts);
}

/** `link_search_unreachable_<shown|click>_<canal>`, con el canal en minúscula (contrato §6). */
export function unreachableEventName(action, channel) {
    return `link_search_unreachable_${action}_${String(channel).toLowerCase()}`;
}

/**
 * Cuenta el `shown` una vez por cuenta mientras el modal está abierto: el aviso se vuelve a dibujar en
 * cada tecla. `seen` es un `Set` que el modal vacía al cerrarse.
 */
export function recordUnreachableShown(seen, channel, row) {
    const key = `${channel}-${row.ad_account_id}`;
    if (seen.has(key)) return;
    seen.add(key);
    ScreenTracker.recordEvent(unreachableEventName('shown', channel));
}

/** Lo que el modal de reconectar necesita de una fila de `client/<id>/ad_accounts`. */
export function reconnectDataOfLinked(row) {
    return {
        ad_account_id: row.ad_account_id,
        name: row.name,
        channel: row.channel,
        agency_channel_credential_label: row.agency_channel_credential_label,
        error_found: row.error_found,
        error_kind: row.error_kind,
        platform_user_name: row.platform_user_name,
        connected_by_user_name: row.connected_by_user_name,
        no_access_since_at: row.no_access_since_at,
    };
}

/**
 * El contenido del vacío del combo cuando lo buscado es una cuenta vinculada sin acceso: el texto y
 * «Reconectarla», que abre el modal de reconectar de esa cuenta.
 */
export function UnreachableLinkedNotice({t, language, row, channel, onReconnect}) {
    return (
        <span className="link-account-unreachable" data-testid="link-account-unreachable">
            <span className="link-account-unreachable__text">{unreachableLinkedText(t, row, language)}</span>
            <button type="button" className="btn -alt link-account-unreachable__btn"
                    onClick={() => {
                        ScreenTracker.recordEvent(unreachableEventName('click', channel));
                        onReconnect(row);
                    }}>
                {t('control_central.link_account_reconnect_it')}
            </button>
        </span>
    );
}
